// File DTO — the wire shape returned by /me/files endpoints (plan.md
// §VI). Mirrors `packages/db/src/schema/content/files.ts`.
import { z } from 'zod';
import { FilePartition, FsName, FsTags } from './folder.js';
import { FileAvailabilityState, FileGroupScope } from './scope.js';
/** 32 lowercase hex chars. */
export const Md5 = z.string().regex(/^[0-9a-f]{32}$/, 'expected lowercase MD5 hex');
/** Type-specific preview metadata. Free-form jsonb; the SPA branches
 *  on `kind` to render the right preview surface. Conservative caps
 *  at the storage layer keep it from growing into stored XSS payloads. */
export const FilePreview = z
    .object({
    kind: z.enum(['image', 'pdf', 'video', 'audio', 'archive', 'text', 'other']),
    /** Image: pixel dimensions. */
    width: z.number().int().nonnegative().optional(),
    height: z.number().int().nonnegative().optional(),
    /** PDF: page count. */
    pageCount: z.number().int().nonnegative().optional(),
    /** Video / audio: duration in seconds (float). */
    durationSeconds: z.number().nonnegative().optional(),
    /** Archive: top-level entry list (truncated). */
    entries: z.array(z.string().max(255)).max(50).optional(),
    /** Text: line count. */
    lineCount: z.number().int().nonnegative().optional(),
    /** Image: stripped EXIF (we do NOT preserve GPS / camera serials —
     *  if a future feature wants those it ships with explicit consent). */
    exif: z.record(z.string(), z.string().max(256)).optional(),
})
    .passthrough();
export const File = z.object({
    id: z.string().uuid(),
    ownerId: z.string().uuid(),
    /** NULL when the file lives at the partition root. */
    folderId: z.string().uuid().nullable(),
    partition: FilePartition,
    name: FsName,
    description: z.string().max(2000).nullable(),
    tags: FsTags,
    mimeType: z.string().min(1).max(255),
    /** Bytes. JSON `number` is precise up to 2^53; we cap requests at
     *  10 GiB so this stays inside that range. */
    sizeBytes: z.number().int().nonnegative(),
    md5: Md5,
    /** Number of saved versions for this file. 1 = no history kept. */
    versionCount: z.number().int().min(1),
    /** Publicly reachable object URL when the active storage driver can
     *  expose one (COS/S3-style object storage). Local / memory drivers
     *  return null and clients fall back to API content routes. */
    publicUrl: z.string().url().nullable(),
    accessCount: z.number().int().nonnegative(),
    lastAccessedAt: z.string().datetime({ offset: true }).nullable(),
    preview: FilePreview.nullable(),
    groupScopes: z.array(FileGroupScope),
    availabilityState: FileAvailabilityState,
    nextAvailableAt: z.string().datetime({ offset: true }).nullable(),
    expiresAt: z.string().datetime({ offset: true }).nullable(),
    /** ISO 8601 — non-null when the file is in the recycle bin. */
    deletedAt: z.string().datetime({ offset: true }).nullable(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
/** Per-version row returned by /me/files/:id/versions. */
export const FileVersion = z.object({
    id: z.string().uuid(),
    fileId: z.string().uuid(),
    versionNo: z.number().int().min(1),
    sizeBytes: z.number().int().nonnegative(),
    mimeType: z.string().min(1).max(255),
    md5: Md5,
    note: z.string().max(2000).nullable(),
    createdBy: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
/** Hard cap for any single file body in V1. Mirrors the route-layer
 *  bodyLimit and the dedup-init max. */
export const MAX_FILE_BYTES = 10 * 1024 * 1024 * 1024; // 10 GiB
/** Default chunk size announced by upload-init. The SPA may negotiate
 *  a different size by passing it on init; the server clamps to
 *  [1 MiB, 64 MiB]. */
export const DEFAULT_CHUNK_SIZE_BYTES = 8 * 1024 * 1024; // 8 MiB
export const MIN_CHUNK_SIZE_BYTES = 1024;
export const MAX_CHUNK_SIZE_BYTES = 64 * 1024 * 1024;
//# sourceMappingURL=file.js.map